import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money } from "@/lib/api";

const defaultsFor = (item) => {
  const sel = {};
  item.options.forEach((g) => { sel[g.id] = g.required && !g.multi && g.choices[0] ? [g.choices[0].id] : []; });
  return sel;
};

const OptionGroup = ({ g, picked, onPick }) => (
  <fieldset className="mt-4" data-testid={`option-group-${g.name.toLowerCase().replace(/\s+/g, "-")}`}>
    <legend className="eyebrow mb-2">{g.name}{g.required ? " · required" : g.multi ? " · pick any" : ""}</legend>
    <div className="flex flex-wrap gap-2">
      {g.choices.map((c) => {
        const on = picked.includes(c.id);
        return (
          <button key={c.id} type="button" onClick={() => onPick(c.id)} data-testid={`option-choice-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${on ? "border-brand bg-brand-light/50 text-ink" : "border-line text-ink hover:border-brand-mid"}`}>
            {c.name}{c.price_cents ? ` +${money(c.price_cents)}` : ""}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export const ItemDialog = ({ item, onClose, onAdd }) => {
  const [sel, setSel] = useState(() => defaultsFor(item));
  const [qty, setQty] = useState(1);
  const pick = (g, cid) => setSel((s) => {
    const cur = s[g.id] || [];
    if (g.multi) return { ...s, [g.id]: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid] };
    return { ...s, [g.id]: g.required ? [cid] : cur.includes(cid) ? [] : [cid] };
  });
  const choiceIds = Object.values(sel).flat();
  const extra = item.options.flatMap((g) => g.choices).filter((c) => choiceIds.includes(c.id)).reduce((a, c) => a + c.price_cents, 0);
  const unit = item.price_cents + extra;
  const labels = item.options.flatMap((g) => g.choices).filter((c) => choiceIds.includes(c.id)).map((c) => c.name);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-md" data-testid="item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black">{item.name}</DialogTitle>
          <DialogDescription>{item.description}</DialogDescription>
        </DialogHeader>
        {item.options.map((g) => <OptionGroup key={g.id} g={g} picked={sel[g.id] || []} onPick={(cid) => pick(g, cid)} />)}
        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 rounded-full border-2 border-line px-2 py-1">
            <button aria-label="Decrease" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="item-qty-minus" className="p-1"><Minus size={16} /></button>
            <span className="w-6 text-center font-bold" data-testid="item-qty">{qty}</span>
            <button aria-label="Increase" onClick={() => setQty(qty + 1)} data-testid="item-qty-plus" className="p-1"><Plus size={16} /></button>
          </div>
          <button className="btn-primary flex-1" data-testid="item-add-to-cart-btn"
            onClick={() => onAdd({ key: `${item.id}:${choiceIds.sort().join(",")}`, item_id: item.id, name: item.name, choice_ids: choiceIds, labels, unit_cents: unit, qty })}>
            Add · {money(unit * qty)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
